import { UNIDADES } from "@caint/data";
import type { AbrirCicloDTO, CicloPublico } from "@caint/shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EditarCicloForm } from "../../components/EditarCicloForm.tsx";
import { FormularioPublicoPainel } from "../../components/FormularioPublicoPainel.tsx";
import { FILTRO_CICLOS_VAZIO, type FiltroCiclos, filtrarCiclos, opcoesFiltro } from "../../lib/ciclos.ts";
import { mensagemAmigavel } from "../../lib/erros.ts";
import { filtrarUnidades } from "../../lib/unidades.ts";
import type { CiclosApi } from "../../state/useAppState.ts";
import { type Coluna, Field, Modal, PageHeader, Tabela } from "../ui/kit.tsx";

export interface CiclosDesktopProps {
  ciclosApi: CiclosApi;
  recarregar: () => Promise<void>;
  podeAbrir: boolean;
  /** Nome do ator (gestor) — trilha do escopo ao editar. */
  autor: string;
  /** Avaliação do ciclo atualmente ativo (destaque + desabilita "Ativar"). */
  avaliacaoIdAtivo?: string | null;
  /** Ativa o ciclo como ciclo global (passa a avaliação ao seletor). */
  onAtivar?: (avaliacaoId: string) => void;
}

const MESES_LABEL = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

function msg(e: unknown): string {
  return mensagemAmigavel(e);
}
function fmt(iso: string): string {
  return iso ? new Date(iso).toLocaleDateString("pt-BR") : "—";
}
function maisTrintaDias(base: string): string {
  const d = new Date(`${base}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + 30);
  return d.toISOString().slice(0, 10);
}
function hojeISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export function CiclosDesktop({
  ciclosApi,
  recarregar,
  podeAbrir,
  autor,
  avaliacaoIdAtivo,
  onAtivar,
}: CiclosDesktopProps) {
  const [ciclos, setCiclos] = useState<CicloPublico[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [abrindo, setAbrindo] = useState(false);
  const [painel, setPainel] = useState<CicloPublico | null>(null);
  const [editando, setEditando] = useState<CicloPublico | null>(null);
  const [filtro, setFiltro] = useState<FiltroCiclos>(FILTRO_CICLOS_VAZIO);
  const opc = useMemo(() => opcoesFiltro(ciclos ?? []), [ciclos]);
  const visiveis = useMemo(() => (ciclos ? filtrarCiclos(ciclos, filtro) : []), [ciclos, filtro]);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      setCiclos(await ciclosApi.listar());
    } catch (e) {
      setErro(msg(e));
      setCiclos([]);
    }
  }, [ciclosApi]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const encerrar = async (id: string) => {
    try {
      await ciclosApi.encerrar(id);
      await carregar();
      await recarregar();
    } catch (e) {
      setErro(msg(e));
    }
  };

  const ativo = (c: CicloPublico) => !!c.avaliacaoId && c.avaliacaoId === avaliacaoIdAtivo;
  const colunas: Coluna<CicloPublico>[] = [
    {
      rotulo: "Unidade",
      celula: (c) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <b>{c.unidadeNome}</b>
          {ativo(c) && (
            <span className="ciclo-badge on" style={{ background: "var(--primary)", color: "#fff" }}>
              ativo
            </span>
          )}
        </span>
      ),
    },
    {
      rotulo: "Status",
      largura: "110px",
      celula: (c) =>
        c.status === "aberto" ? (
          <span style={{ color: "var(--mat-4)" }}>aberto</span>
        ) : (
          <span className="spec">encerrado</span>
        ),
    },
    { rotulo: "Início", largura: "120px", celula: (c) => fmt(c.dataInicio) },
    { rotulo: "Fim", largura: "120px", celula: (c) => fmt(c.dataFim) },
    {
      rotulo: "Ações",
      largura: "400px",
      celula: (c) => (
        <div style={{ display: "flex", gap: 6 }}>
          {onAtivar && c.avaliacaoId && (
            <button
              type="button"
              className={`m-btn${ativo(c) ? "" : " primary"}`}
              style={{ padding: "5px 10px", fontSize: 12 }}
              disabled={ativo(c)}
              onClick={() => c.avaliacaoId && onAtivar(c.avaliacaoId)}
            >
              {ativo(c) ? "Ativo" : "Ativar"}
            </button>
          )}
          <button
            type="button"
            className="m-btn"
            style={{ padding: "5px 10px", fontSize: 12 }}
            onClick={() => setPainel(c)}
          >
            Formulário & IMGR
          </button>
          {podeAbrir && (
            <button
              type="button"
              className="m-btn"
              style={{ padding: "5px 10px", fontSize: 12 }}
              onClick={() => setEditando(c)}
            >
              Editar
            </button>
          )}
          {podeAbrir && c.status === "aberto" && (
            <button
              type="button"
              className="m-btn"
              style={{ padding: "5px 10px", fontSize: 12 }}
              onClick={() => void encerrar(c.id)}
            >
              Encerrar
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="dk-page">
      <PageHeader
        titulo="Ciclos de Avaliação"
        sub="Abertura por unidade e período (presidente)"
        acoes={
          podeAbrir ? (
            <button type="button" className="m-btn primary" onClick={() => setAbrindo(true)}>
              + Abrir ciclo
            </button>
          ) : undefined
        }
      />
      {erro && (
        <p
          style={{
            color: "var(--danger)",
            background: "var(--danger-soft)",
            borderRadius: 10,
            padding: "8px 12px",
            marginBottom: 12,
          }}
        >
          {erro}
        </p>
      )}
      {ciclos && ciclos.length > 0 && (
        <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <select
            className="m-select"
            aria-label="Ano"
            style={{ width: 130 }}
            value={filtro.ano ?? ""}
            onChange={(e) => setFiltro({ ...filtro, ano: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">Todos os anos</option>
            {opc.anos.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
          <select
            className="m-select"
            aria-label="Mês"
            style={{ width: 130 }}
            value={filtro.mes ?? ""}
            onChange={(e) => setFiltro({ ...filtro, mes: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">Todos os meses</option>
            {opc.meses.map((m) => (
              <option key={m} value={m}>
                {MESES_LABEL[m - 1]}
              </option>
            ))}
          </select>
          <select
            className="m-select"
            aria-label="Unidade"
            style={{ minWidth: 220 }}
            value={filtro.unidadeId ?? ""}
            onChange={(e) => setFiltro({ ...filtro, unidadeId: e.target.value || null })}
          >
            <option value="">Todas as unidades</option>
            {opc.unidades.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </select>
        </div>
      )}
      <Tabela
        colunas={colunas}
        linhas={visiveis}
        chave={(c) => c.id}
        vazio={
          ciclos === null
            ? "Carregando…"
            : ciclos.length === 0
              ? "Nenhum ciclo ainda."
              : "Nenhum ciclo para os filtros escolhidos."
        }
      />
      {abrindo && (
        <AbrirCicloModal
          ciclosApi={ciclosApi}
          onClose={() => setAbrindo(false)}
          onAberto={async () => {
            setAbrindo(false);
            await carregar();
            await recarregar();
          }}
        />
      )}
      {painel && (
        <Modal titulo="Formulário público" sub={painel.unidadeNome} onClose={() => setPainel(null)}>
          <FormularioPublicoPainel ciclo={painel} carregarResultado={ciclosApi.resultado} />
        </Modal>
      )}
      {editando && (
        <Modal titulo="Editar ciclo" sub={editando.unidadeNome} onClose={() => setEditando(null)}>
          <EditarCicloForm
            ciclo={editando}
            ciclosApi={ciclosApi}
            autor={autor}
            onSaved={async () => {
              setEditando(null);
              await carregar();
              await recarregar();
            }}
          />
        </Modal>
      )}
    </div>
  );
}

function AbrirCicloModal({
  ciclosApi,
  onClose,
  onAberto,
}: { ciclosApi: CiclosApi; onClose: () => void; onAberto: () => void }) {
  const [tipo, setTipo] = useState("");
  const [busca, setBusca] = useState("");
  const tipos = useMemo(() => [...new Set(UNIDADES.map((u) => u.tipo))].sort(), []);
  const filtradas = useMemo(() => filtrarUnidades(tipo, busca), [tipo, busca]);
  const [unidadeId, setUnidadeId] = useState(UNIDADES[0]?.id ?? "");
  const [inicio, setInicio] = useState(hojeISO());
  const [fim, setFim] = useState(maisTrintaDias(hojeISO()));
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  // Mantém a seleção válida quando o filtro muda.
  useEffect(() => {
    if (!filtradas.some((u) => u.id === unidadeId)) setUnidadeId(filtradas[0]?.id ?? "");
  }, [filtradas, unidadeId]);

  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    const dto: AbrirCicloDTO = {
      unidadeId,
      dataInicio: new Date(`${inicio}T00:00:00.000Z`).toISOString(),
      dataFim: new Date(`${fim}T23:59:59.000Z`).toISOString(),
    };
    try {
      await ciclosApi.abrir(dto);
      onAberto();
    } catch (e) {
      setErro(msg(e));
      setSalvando(false);
    }
  };

  return (
    <Modal
      titulo="Abrir ciclo"
      sub="Unidade e período de avaliação"
      onClose={onClose}
      foot={
        <button
          type="button"
          className="m-btn primary"
          disabled={salvando || !unidadeId}
          onClick={() => void salvar()}
        >
          {salvando ? "Abrindo…" : "Abrir ciclo"}
        </button>
      }
    >
      {erro && <p style={{ color: "var(--danger)", marginBottom: 10 }}>{erro}</p>}
      <Field id="ciq-tipo" rotulo="Tipo">
        <select id="ciq-tipo" className="m-select" value={tipo} onChange={(e) => setTipo(e.target.value)}>
          <option value="">Todos os tipos</option>
          {tipos.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </Field>
      <Field id="ciq-busca" rotulo="Buscar">
        <input
          id="ciq-busca"
          className="m-input"
          placeholder="Nome ou município"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </Field>
      <Field id="ciq-un" rotulo={`Unidade (${filtradas.length})`}>
        <select
          id="ciq-un"
          className="m-select"
          value={unidadeId}
          onChange={(e) => setUnidadeId(e.target.value)}
        >
          {filtradas.map((u) => (
            <option key={u.id} value={u.id}>
              {u.nome} · {u.municipio}
            </option>
          ))}
        </select>
      </Field>
      <Field id="ciq-ini" rotulo="Início">
        <input
          id="ciq-ini"
          type="date"
          className="m-input"
          value={inicio}
          onChange={(e) => setInicio(e.target.value)}
        />
      </Field>
      <Field id="ciq-fim" rotulo="Fim">
        <input
          id="ciq-fim"
          type="date"
          className="m-input"
          value={fim}
          onChange={(e) => setFim(e.target.value)}
        />
      </Field>
    </Modal>
  );
}
